import React, { useState } from 'react';
import { AlertTriangle, Users, UserMinus, CreditCard } from 'lucide-react';
import { Button } from './button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './dialog';

interface UnregisterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (leaveTeam: boolean) => void;
  eventTitle: string;
  eventType: 'hackathon' | 'event';
  eventFee?: number;
  isPaidEvent?: boolean;
  isInTeam?: boolean;
  isTeamLeader?: boolean;
  loading?: boolean;
}

const UnregisterDialog: React.FC<UnregisterDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  eventTitle,
  eventType,
  eventFee,
  isPaidEvent = false,
  isInTeam = false,
  isTeamLeader = false,
  loading = false
}) => {
  const [leaveTeam, setLeaveTeam] = useState(true);

  const handleConfirm = () => {
    onConfirm(leaveTeam);
  };

  const handleClose = () => {
    setLeaveTeam(true); // Reset to default
    onClose();
  };

  const isHackathon = eventType === 'hackathon';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Unregister from {isHackathon ? 'Hackathon' : 'Event'}
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to unregister from <strong>{eventTitle}</strong>?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Payment Warning */}
          {isPaidEvent && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CreditCard className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-red-800">Payment Notice</h4>
                  <p className="text-sm text-red-700 mt-1">
                    <strong>Amount paid will not be reimbursed.</strong> The registration fee of ₹{eventFee} INR is non-refundable.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isHackathon && isInTeam && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="space-y-2">
                  <h4 className="font-medium text-yellow-800">You are currently in a team</h4>
                  <p className="text-sm text-yellow-700">
                    {isTeamLeader 
                      ? "As the team leader, you can choose to leave the team or stay in the team while unregistering from the hackathon."
                      : "You can choose to leave the team or stay in the team while unregistering from the hackathon."
                    }
                  </p>
                  
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="teamOption"
                        checked={leaveTeam}
                        onChange={() => setLeaveTeam(true)}
                        className="text-blue-600"
                      />
                      <span className="text-sm font-medium text-yellow-800">
                        Leave team and unregister
                      </span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="teamOption"
                        checked={!leaveTeam}
                        onChange={() => setLeaveTeam(false)}
                        className="text-blue-600"
                      />
                      <span className="text-sm font-medium text-yellow-800">
                        Stay in team but unregister from hackathon
                      </span>
                    </label>
                  </div>
                  
                  {isTeamLeader && leaveTeam && (
                    <div className="bg-red-50 border border-red-200 rounded p-2">
                      <p className="text-xs text-red-700">
                        ⚠️ As the team leader, leaving the team will either assign a new leader or disband the team if no other members remain.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <UserMinus className="h-5 w-5 text-gray-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-800">What happens when you unregister?</h4>
                <ul className="text-sm text-gray-700 mt-2 space-y-1">
                  <li>• Your registration will be removed from the {isHackathon ? 'hackathon' : 'event'}</li>
                  <li>• You won't be able to participate in the {isHackathon ? 'hackathon' : 'event'}</li>
                  {isHackathon && leaveTeam && isInTeam && (
                    <li>• You will be removed from your team</li>
                  )}
                  {isHackathon && !leaveTeam && isInTeam && (
                    <li>• You will stay in your team but won't be registered for the hackathon</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Unregistering...' : 'Unregister'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UnregisterDialog; 